import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { apiUrl } from '../../lib/config';

interface WhitelistedUser {
  characterId: number;
  characterName: string;
  corporationName?: string;
  allianceName?: string;
  addedBy: number;
  createdAt: number;
}

interface SearchResult {
  id: number;
  name: string;
}

export default function AdminPage() {
  const { isAdmin, isLoading: authLoading } = useAuthStore();
  const navigate = useNavigate();

  const [users, setUsers] = useState<WhitelistedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<number | null>(null);

  // Redirect non-admins (but only after auth has loaded)
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, authLoading, navigate]);

  // Fetch whitelist
  const fetchWhitelist = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('/api/admin/whitelist'), { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      } else {
        setError('Failed to load whitelist');
      }
    } catch {
      setError('Failed to load whitelist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchWhitelist();
    }
  }, [isAdmin, fetchWhitelist]);

  // Search characters (debounced)
  const searchCharacters = useCallback(async (query: string) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(
        apiUrl(`/api/admin/characters/search?q=${encodeURIComponent(query)}`),
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.characters || []);
      }
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchCharacters(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchCharacters]);

  // Add user to whitelist
  const addUser = async (characterId: number, characterName: string) => {
    setAddingId(characterId);
    setError(null);

    try {
      const response = await fetch(apiUrl('/api/admin/whitelist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ characterId, characterName }),
      });

      if (response.ok) {
        await fetchWhitelist();
        setSearchQuery('');
        setSearchResults([]);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to add user');
      }
    } catch {
      setError('Failed to add user');
    } finally {
      setAddingId(null);
    }
  };

  // Remove user from whitelist
  const removeUser = async (characterId: number, characterName: string) => {
    if (!confirm(`Remove ${characterName} from whitelist?`)) return;

    try {
      const response = await fetch(apiUrl(`/api/admin/whitelist/${characterId}`), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        await fetchWhitelist();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to remove user');
      }
    } catch {
      setError('Failed to remove user');
    }
  };

  // Check if character is already whitelisted
  const isWhitelisted = (characterId: number) => {
    return users.some((u) => u.characterId === characterId);
  };

  if (authLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Admin - Whitelist Management</h1>
        <p className="text-gray-400">Search for EVE characters and add them to the whitelist.</p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-900/50 p-4 text-red-200">
          {error}
          <button className="ml-4 text-red-400 hover:text-red-300" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* Search Section */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Add User</h2>
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for a character name (exact match)..."
            className="w-full rounded-lg border border-gray-600 bg-gray-700 px-4 py-3 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
          />
          {searching && (
            <div className="absolute right-3 top-3 text-gray-400">Searching...</div>
          )}
        </div>

        {searchQuery.length > 0 && searchQuery.length < 3 && (
          <p className="mt-2 text-sm text-gray-500">Enter at least 3 characters to search</p>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-2 rounded-lg border border-gray-600 bg-gray-800">
            {searchResults.map((char) => (
              <div
                key={char.id}
                className="flex items-center justify-between p-3 border-b border-gray-700 last:border-b-0"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={`https://images.evetech.net/characters/${char.id}/portrait?size=32`}
                    alt={char.name}
                    className="w-8 h-8 rounded"
                  />
                  <span className="text-white">{char.name}</span>
                </div>
                {isWhitelisted(char.id) ? (
                  <span className="text-green-400 text-sm">Already whitelisted</span>
                ) : (
                  <button
                    onClick={() => addUser(char.id, char.name)}
                    disabled={addingId === char.id}
                    className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {addingId === char.id ? 'Adding...' : 'Add'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {searchQuery.length >= 3 && !searching && searchResults.length === 0 && (
          <p className="mt-2 text-sm text-gray-500">
            No characters found. ESI requires an exact character name match.
          </p>
        )}
      </div>

      {/* Whitelist Table */}
      <div>
        <h2 className="text-lg font-semibold mb-4">
          Whitelisted Users ({users.length})
        </h2>

        {loading ? (
          <div className="text-gray-400">Loading whitelist...</div>
        ) : users.length === 0 ? (
          <div className="rounded-lg bg-gray-800 p-6 text-center text-gray-400">
            No users whitelisted yet. Search for characters above to add them.
          </div>
        ) : (
          <div className="rounded-lg border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                    Character
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                    Corporation
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                    Added
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {users.map((user) => (
                  <tr key={user.characterId} className="bg-gray-800/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={`https://images.evetech.net/characters/${user.characterId}/portrait?size=32`}
                          alt={user.characterName}
                          className="w-8 h-8 rounded"
                        />
                        <span className="text-white">{user.characterName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {user.corporationName || '—'}
                      {user.allianceName && (
                        <span className="text-gray-500"> [{user.allianceName}]</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {new Date(user.createdAt * 1000).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => removeUser(user.characterId, user.characterName)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
